import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { hasRequiredValidator } from '@shared/general';
import { Subscription } from 'rxjs';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'dropdown',
  standalone: true,
  imports: [NgClass, SvgIconComponent],
  templateUrl: './dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DropdownComponent,
    },
  ],
})
export class DropdownComponent implements OnChanges, OnInit, OnDestroy {
  @Input() options: any[] = [];
  @Input() placeholder = '';
  @Input() keys?: { value: string; label: string };
  @Input() disabled: boolean = false;
  @Input() icon: string = '';
  @Input() size: 'S' | 'M' | 'L' = 'L';
  @Input() corners: boolean = false;
  @Input() value: string = '';
  @Input() error: string = '';
  @Input() label: string = '';
  @Input() control: FormControl = new FormControl(null);
  @ViewChild('dropdownWrapper') dropdownWrapper?: ElementRef;

  selectedLabel: string = '';
  showOptions: boolean = false;
  subsription?: Subscription;

  @HostListener('document:click', ['$event'])
  closeDropdownOnClickOutside(event: MouseEvent) {
    const clickedInside = this.dropdownWrapper?.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showOptions = false;
    }
  }

  get isRequired(): boolean {
    return hasRequiredValidator(this.control);
  }

  private onChange: (value: string) => void = () => {
    if (this.control) {
      this.control.markAsTouched({ onlySelf: false, emitEvent: true });
      this.control.markAsDirty({ onlySelf: false, emitEvent: true });
    }
  };

  private onTouched: () => void = () => {};

  ngOnInit() {
    this.subsription = this.control.valueChanges.subscribe((val) => {
      this.updateLabel(val);
    });

    if (this.control.value && this.options.length) {
      this.setValue(this.control.value);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'].previousValue !== changes['options'].currentValue && !changes['options'].firstChange) {
      this.control.setValue(undefined);
      this.selectedLabel = '';
    }
  }

  ngOnDestroy() {
    this.subsription?.unsubscribe();
  }

  openDropdown() {
    this.showOptions = !this.showOptions;
    this.control.markAsTouched();
  }

  setValue(value: string | number | boolean, emit: boolean = false) {
    const option = this.options.find((op) => (this.keys ? op[`${this.keys?.value}`] : op) === value);

    if (this.keys) {
      const val = option?.[this.keys?.value];
      if (val !== undefined && typeof val !== 'object') {
        this.control?.setValue(val);
        if (emit) this.onChange(val);
      }
    } else {
      this.control?.setValue(option);
      if (emit) this.onChange(option);
    }

    this.showOptions = false;
  }

  updateLabel(val: string | number | boolean) {
    const option = this.options.find((op) => (this.keys ? op[`${this.keys?.value}`] : op) === val);
    if (option) {
      this.selectedLabel = this.keys ? option[this.keys.label] : val;
    } else {
      this.selectedLabel = '';
    }
  }

  writeValue(value: string): void {
    this.value = value || '';
  }

  getFirstErrorMessage(errors: any): string | null {
    if (this.control.touched && (!!errors || Object.keys(errors || {}).length)) {
      const errorKey = Object.keys(errors)[0];
      if (errorKey === 'required') {
        return `This field must be filled`;
      } else {
        return `${errors[errorKey]}`;
      }
    }
    return null;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
